import { Challenge, DailyActivity } from '../types';
import { getActivityMiles, isWorkoutSplitMilesActivity } from '../utils/activityUtils';

/**
 * Activity shape needed to evaluate challenge requirements (from a saved doc or form).
 * Used after logging daily activity — see challengeService.processActivityForChallenges.
 */
export type ChallengeMatchActivity = Pick<
  DailyActivity,
  | 'runType'
  | 'runSubType'
  | 'milesRun'
  | 'warmupMiles'
  | 'workoutMiles'
  | 'cooldownMiles'
  | 'crossTrainingMinutes'
  | 'strengthMinutes'
>;

/** Challenge UI value "Workout" = legacy top-level Workout OR Run with Workout subtype. */
export function activityMatchesChallengeActivityType(
  activity: ChallengeMatchActivity,
  required?: string
): boolean {
  const t = (required || '').trim();
  if (!t) return true;

  const rt = activity.runType;

  switch (t) {
    case 'Run':
      if (rt !== 'Run') return false;
      return activity.runSubType !== 'Workout';
    case 'Workout':
      return isWorkoutSplitMilesActivity(activity);
    case 'Cross Training':
      return rt === 'Cross Training';
    case 'Strength':
      return rt === 'Strength';
    default:
      return rt === t;
  }
}

/**
 * minDistance (miles) applies to mile-based logs; for cross training / strength, total "distance"
 * requirement is not well-defined — we treat minDistance as minutes for those types when set.
 */
export function activityMatchesChallengeMinDistance(
  activity: ChallengeMatchActivity,
  minDistance?: number
): boolean {
  if (minDistance === undefined || minDistance === null || minDistance <= 0) return true;

  if (activity.runType === 'Cross Training') {
    return (activity.crossTrainingMinutes || 0) >= minDistance;
  }
  if (activity.runType === 'Strength') {
    return (activity.strengthMinutes || 0) >= minDistance;
  }

  const miles = getActivityMiles(activity);
  return miles >= minDistance;
}

/** When minDuration is set: CT/ST use minutes; run/workout use ~8 min/mile from logged distance. */
export function activityMatchesChallengeMinDuration(
  activity: ChallengeMatchActivity,
  minDuration?: number
): boolean {
  if (minDuration === undefined || minDuration === null || minDuration <= 0) return true;

  if (activity.runType === 'Cross Training') {
    return (activity.crossTrainingMinutes || 0) >= minDuration;
  }
  if (activity.runType === 'Strength') {
    return (activity.strengthMinutes || 0) >= minDuration;
  }

  const miles = getActivityMiles(activity);
  return miles * 8 >= minDuration;
}

export function activityMatchesChallengeRequirements(
  activity: ChallengeMatchActivity,
  requirements: Challenge['requirements']
): boolean {
  if (!requirements) return true;
  if (!activityMatchesChallengeActivityType(activity, requirements.activityType)) return false;
  if (!activityMatchesChallengeMinDistance(activity, requirements.minDistance)) return false;
  if (!activityMatchesChallengeMinDuration(activity, requirements.minDuration)) return false;
  // consecutiveDays: not evaluated here yet
  return true;
}
